import React, { useCallback } from 'react';
import {
  TemplateMessageModal,
  TemplateMessageModalBody,
  TemplateMessageModalFooter,
  TemplateMessageModalHeader,
  TemplateMessageSelectable,
  TemplateMessageSelectableBody,
  TemplateMessageSelectableFooter,
  TemplateMessageSelectableHeader
} from 'components/Navbars/ServicePanel/DialerCallInformation/form/TemplateMessagesModal/styled';
import { IoIosLink, IoMdMail } from 'react-icons/io';
import { useSelector } from 'react-redux';
import { Button, Modal } from 'reactstrap';
import { FlexMediaPreviewer } from 'components/FlexMediaPreviewer';
import { getFileFromUrl } from 'utils/fuctions/getFileFromUrl';

/**
 * Representa uma mensagem pré-definida a ser utilizada no módulo de Protocolo
 *
 * @typedef TemplateMessage
 * @property {String[]} channel - A lista de canais aos quais a mensagem pré-definida está vinculada.
 * @property {String | null} messageHtml - A mensagem que será encaminhada pelo email, em forma de html
 * @property {String} message - A mensagem que será enviada pelo whatsapp, em forma de html
 * @property {Number[]} teams - As equipes que estão vinculados à mensagem pré-definida
 * @property {String} title - O título da mensagem pré-definida
 * @property {String} preDefinedMessageMidiaUrl - A URL da mídia vinculada à mensagem pré-definida
 * @property {"1" | "2" | "3" | "" |  null} whenToSend - Determina se a mídia deve ser enviada antes, em conjunto, ou depois do texto da mensagem. 1 - Antes da mensagem. 2 - Junto da mensagem. 3 - Depois da mensagem.
 * @property {String} id - O identificador único da mensagem pré-definida
 */

/**
 * Renderiza um modal que permite escolher um template de mensagem pré-definida.
 * @see {@link TemplateMessage}
 *
 * @param {Object} props - O objeto de propriedades.
 * @param {(transformedMessage: any) => void} props.setPredefinedMessage - Função para definir a mensagem pré-definida selecionada.
 * @param {TemplateMessage[]} [props.predefinedMessages=[]] - Array de mensagens pré-definidas.
 * @param {("messageHtml" | "message")[]} [props.messageFieldsToRender=["messageHtml"]] - Quais campos devem ser renderizados no modal. Por padrão renderiza apenas a mensagem de email.
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setIsOpen - Função para alternar a visibilidade do modal.
 * @param {boolean} props.open - Booleano indicando se o modal está aberto ou não.
 * @param {(a: TemplateMessage) => any} [props.transformResult] - Um callback que transforma o resultado de acordo com a necessidade do elemento pai. É chamado antes de aplicar o setter. Por padrão é uma função identidade.
 * @returns {JSX.Element} O componente modal renderizado.
 */
export const PredefinedMessageModal = ({
  setPredefinedMessage,
  messageFieldsToRender = ['messageHtml'],
  predefinedMessages = [],
  setIsOpen,
  open,
  transformResult = (a) => a
}) => {
  const toggleModal = useCallback(() => setIsOpen(!open), [open, setIsOpen]);

  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  return (
    <>
      <Modal
        className={`modal-dialog-centered`}
        isOpen={open}
        toggle={toggleModal}
        size="xl"
      >
        <TemplateMessageModal>
          <TemplateMessageModalHeader>
            <p>Selecione uma mensagem pré-definida</p>
            <button
              aria-label="Close"
              className="close"
              data-dismiss="modal"
              type="button"
              onClick={toggleModal}
            >
              <span aria-hidden={true}>×</span>
            </button>
          </TemplateMessageModalHeader>
          <TemplateMessageModalBody>
            {predefinedMessages
              .filter((message) => {
                return messageFieldsToRender.every((field) => message[field]);
              })
              .map((message, index) => (
                <TemplateMessageSelectable
                  key={index}
                  onClick={() => {
                    const transformedMessage = transformResult(message);

                    setPredefinedMessage(transformedMessage);
                    toggleModal();
                  }}
                >
                  <TemplateMessageSelectableHeader>
                    <span>{message.title}</span>
                    <span>
                      {message.whenToSend && 'Envio da mídia: '}
                      {message.whenToSend == '1' ? (
                        <span style={{ fontWeight: 'normal' }}>
                          antes da mensagem
                        </span>
                      ) : null}
                      {message.whenToSend == '2' ? (
                        <span style={{ fontWeight: 'normal' }}>
                          junto da mensagem
                        </span>
                      ) : null}
                      {message.whenToSend == '3' ? (
                        <span style={{ fontWeight: 'normal' }}>
                          depois da mensagem
                        </span>
                      ) : null}
                    </span>
                  </TemplateMessageSelectableHeader>
                  <TemplateMessageSelectableBody>
                    {messageFieldsToRender.includes('message') ? (
                      <div>
                        <span>
                          <IoMdMail color={systemConfiguration.primary_color} />{' '}
                          Mensagem do Whatsapp
                        </span>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: message.message
                          }}
                        />
                      </div>
                    ) : null}
                    {messageFieldsToRender.includes('messageHtml') ? (
                      <div>
                        <span>
                          <IoMdMail color={systemConfiguration.primary_color} />{' '}
                          Mensagem do Email
                        </span>

                        <div
                          dangerouslySetInnerHTML={{
                            __html: message.messageHtml
                          }}
                        />
                      </div>
                    ) : null}
                  </TemplateMessageSelectableBody>
                  <TemplateMessageSelectableFooter>
                    {message.preDefinedMessageMidiaUrl ? (
                      <>
                        <FlexMediaPreviewer file={getFileFromUrl(message.preDefinedMessageMidiaUrl).then(res => res)} />
                        <div>
                          <span>
                            <IoIosLink
                              color={systemConfiguration.primary_color}
                            />
                            URL da mídia:{' '}
                          </span>
                          <a href={message.preDefinedMessageMidiaUrl}>
                            {message.preDefinedMessageMidiaUrl}
                          </a>
                        </div>
                      </>
                    ) : null}
                  </TemplateMessageSelectableFooter>
                </TemplateMessageSelectable>
              ))}
          </TemplateMessageModalBody>
          <TemplateMessageModalFooter>
            <Button
              data-dismiss="modal"
              color="danger"
              type="button"
              onClick={toggleModal}
            >
              Fechar
            </Button>
          </TemplateMessageModalFooter>
        </TemplateMessageModal>
      </Modal>
    </>
  );
};
